import { useMutation } from '@apollo/client';
import {
  Modal,
  PrimaryButton,
  Stack,
  Toggle,
  Text,
  ContextualMenu,
  Spinner,
  Tooltip,
  TooltipHost,
} from '@fluentui/react';
import { cutToast } from 'utility/toast';
import { useState } from 'react';
import {
  PreviewApprovalTesting,
  PreviewApprovalTestingVariables,
} from '../__generated__/PreviewApprovalTesting';

import PREVIEW_TEST from '../PreviewApprovalTesting.graphql';
import {
  HierarchyNode,
  TestHierarchyFlow,
} from '../Test/FormView/PreviewApprovalHierarchyReactFlow';
import { NodeView } from '../Test/FormView/PreviewApprovalHierarchyReactFlow/NodeView';
import { CloseButton } from 'common/components/Buttons';
import { useStyles } from './index.styles';
import { useFormContext, useWatch } from 'react-hook-form';
import { TestCaseValues } from '../Test/types';
import { useCommonStyles } from 'common/styles';
import ApprovedIcon from '../../../../../../../assets/svgs/ApproveStatusIcons/Approved.svg?react';
import DeclinedIcon from '../../../../../../../assets/svgs/ApproveStatusIcons/Declined.svg?react';

const LOCAL_TESTING_TOOLTIP = (
  <span>
    <b>Local testing</b> always uses the current approval flow and the test
    case's transactional values.
  </span>
);
const GLOBAL_TESTING_TOOLTIP = (
  <span>
    <b>Global testing</b> emulates the behavior of an approval request being
    made on a transaction. Using the test values, the approval engine cycles
    through the list of approval flows—based on execution priority and
    conditional values—selects the appropriate approval flow to process.
  </span>
);

const GLOBAL_TEST_WARNING_MESSAGE =
  'This approval must be either Approved or Authorized to run a Global test.';

export const RunTest: React.FC<{
  requestApprovalId: string;
  approvalTestResultId: string;
  onSuccess: () => void;
  allowGlobalTest: boolean | undefined | null;
}> = (props) => {
  const { getValues } = useFormContext<TestCaseValues>();
  const isGlobalTest = useWatch({ name: 'isGlobalTest' });
  const [global, setGlobal] = useState<boolean>(!!isGlobalTest);
  const [visible, setVisible] = useState(false);
  const [rawData, setRawData] = useState<HierarchyNode[]>([]);
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const [topic, setTopic] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const [runTest, { loading }] = useMutation<
    PreviewApprovalTesting,
    PreviewApprovalTestingVariables
  >(PREVIEW_TEST);

  const onClick = async () => {
    if (global && !props.allowGlobalTest) {
      cutToast.error(GLOBAL_TEST_WARNING_MESSAGE);
      return;
    }
    const { errors, data } = await runTest({
      variables: {
        id: props.requestApprovalId,
        input: {
          approvalTestResultId: props.approvalTestResultId,
          requestApprovalId: props.requestApprovalId,
          isGlobalTest: global,
        },
      },
    });
    if (!!errors?.length) cutToast.error(errors[0].message);
    else {
      props.onSuccess();
      if (data?.previewApprovalTesting?.approvalMapTypes)
        setRawData(data?.previewApprovalTesting?.approvalMapTypes);
      const approvalMapTypeObject =
        data?.previewApprovalTesting?.approvalMapTypes?.[0];
      setTopic(approvalMapTypeObject?.topic ?? '');
      setSuccess(
        approvalMapTypeObject?.id === props.requestApprovalId &&
          !approvalMapTypeObject?.errorMessage
      );
      setVisible(true);
      cutToast.success('Test Run was successful');
    }
  };
  return (
    <>
      <Stack horizontal tokens={{ childrenGap: 30 }} verticalAlign="center">
        <TooltipHost
          content={global ? GLOBAL_TESTING_TOOLTIP : LOCAL_TESTING_TOOLTIP}
        >
          <Toggle
            inlineLabel
            label="Global Test"
            checked={global}
            onChange={() => {
              setGlobal((prev) => !prev);
            }}
          />
        </TooltipHost>
        <PrimaryButton
          disabled={loading}
          onClick={onClick}
          iconProps={{ iconName: loading ? undefined : 'Play' }}
        >
          {loading ? <Spinner /> : 'Run Test'}
        </PrimaryButton>
      </Stack>
      {visible && (
        <Modal
          isOpen
          scrollableContentClassName={styles.noScrollContainer}
          containerClassName={styles.modalContainer}
          dragOptions={{
            moveMenuItemText: 'Move',
            closeMenuItemText: 'Close',
            menu: ContextualMenu,
            dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
          }}
        >
          <Stack>
            <Stack className={styles.headerContainer} tokens={{ padding: 25 }}>
              <Stack
                horizontal
                className={styles.alignItemsCenter}
                tokens={{ childrenGap: 10 }}
              >
                {success ? <ApprovedIcon /> : <DeclinedIcon />}
                <Text variant="xLarge">Test Results</Text>
                <Text
                  variant="xLarge"
                  className={commonStyles.colorThemePrimary}
                >
                  {getValues('comment') + ' (' + topic + ')'}
                </Text>
                <Stack style={{ marginLeft: 'auto' }}>
                  <CloseButton onClick={() => setVisible(false)} />
                </Stack>
              </Stack>
            </Stack>
            <TestHierarchyFlow
              rawData={rawData}
              NodeViewComponent={(props) => (
                <NodeView
                  nodeData={props.data as unknown as HierarchyNode}
                  key={props.data.id}
                />
              )}
            />
          </Stack>
        </Modal>
      )}
    </>
  );
};
